import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ColorOverridePicker from "@/components/ColorOverridePicker";
import BackgroundUploader from "@/components/BackgroundUploader";
import { DEFAULT_CONFETTI, BUTTON_RADIUS } from "@/lib/gameStyle";

/**
 * AppearanceCustomizer — shared editor block (admin + merchant) for the
 * public-page look & feel: button colors/shape/labels, background overlay
 * intensity, forced light/dark mode, confetti colors, wheel hub logo.
 */
export default function AppearanceCustomizer({ game, patch, testIdPrefix = "appearance" }) {
  const { t } = useTranslation();
  const p = testIdPrefix;
  const overlay = game.bg_overlay_opacity == null ? 55 : game.bg_overlay_opacity;

  return (
    <div className="space-y-5">
      <p className="text-[11px] text-muted-foreground leading-snug">
        {t(
          "game_editor.appearance_hint",
          "Personnalisez les boutons, l'assombrissement du fond, le mode d'affichage et les effets de victoire de la page de jeu publique."
        )}
      </p>

      {/* Button colors */}
      <div className="grid grid-cols-2 gap-3">
        <ColorOverridePicker
          label={t("game_editor.button_bg", "Fond des boutons")}
          value={game.button_bg_color}
          onChange={(v) => patch({ button_bg_color: v })}
          previewText={game.button_continue_label || t("game_editor.preview_continue")}
          testIdPrefix={`${p}-button-bg`}
        />
        <ColorOverridePicker
          label={t("game_editor.button_text", "Texte des boutons")}
          value={game.button_text_color}
          onChange={(v) => patch({ button_text_color: v })}
          previewText={game.button_continue_label || t("game_editor.preview_continue")}
          testIdPrefix={`${p}-button-text`}
        />
        <ColorOverridePicker
          label={t("game_editor.button_bg_2", "Dégradé — 2ᵉ couleur")}
          value={game.button_bg_color_2}
          onChange={(v) => patch({ button_bg_color_2: v })}
          previewText={game.button_continue_label || t("game_editor.preview_continue")}
          testIdPrefix={`${p}-button-bg-2`}
        />
        {game.type === "quiz" && (
          <ColorOverridePicker
            label={t("game_editor.quiz_select_color", "Réponse sélectionnée (quiz)")}
            value={game.quiz_select_color}
            onChange={(v) => patch({ quiz_select_color: v })}
            previewText={t("game_editor.quiz_select_color_preview", "✓ Réponse choisie")}
            testIdPrefix={`${p}-quiz-select`}
          />
        )}
        {game.button_bg_color_2 && (
          <div className="flex flex-col justify-end">
            <div className="text-[11px] text-muted-foreground mb-1.5">
              {t("game_editor.button_bg_2_hint", "Aperçu du dégradé")}
            </div>
            <div
              className="h-10 rounded-xl flex items-center justify-center text-xs font-bold text-white"
              style={{
                background: `linear-gradient(135deg, ${game.button_bg_color || game.theme_color || "#8B5CF6"} 0%, ${game.button_bg_color_2} 100%)`,
                color: game.button_text_color || "#fff",
              }}
              data-testid={`${p}-gradient-preview`}
            >
              {game.button_continue_label || t("game_editor.preview_continue")}
            </div>
          </div>
        )}
      </div>

      {/* Button shape + live chip */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
          {t("game_editor.button_radius", "Forme des boutons")}
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={game.button_radius || "default"}
            onValueChange={(v) =>
              patch({ button_radius: v === "default" ? null : v })
            }
          >
            <SelectTrigger
              className="h-9 rounded-lg text-[13px] w-[190px]"
              data-testid={`${p}-radius-select`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">
                {t("game_editor.radius_default", "Par défaut (arrondi)")}
              </SelectItem>
              <SelectItem value="pill">
                {t("game_editor.radius_pill", "Pilule")}
              </SelectItem>
              <SelectItem value="square">
                {t("game_editor.radius_square", "Carré")}
              </SelectItem>
            </SelectContent>
          </Select>
          <div
            className="h-9 px-4 inline-flex items-center text-xs font-medium"
            style={{
              backgroundColor: game.button_bg_color || "hsl(var(--foreground))",
              color: game.button_text_color || "hsl(var(--background))",
              borderRadius:
                BUTTON_RADIUS[game.button_radius] || BUTTON_RADIUS.rounded,
            }}
            data-testid={`${p}-radius-preview`}
          >
            {game.button_continue_label || t("game_editor.preview_continue")}
          </div>
        </div>
      </div>

      {/* Custom button labels */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
            {t("game_editor.continue_label", "Texte du bouton « Continuer »")}
          </div>
          <Input
            value={game.button_continue_label || ""}
            onChange={(e) =>
              patch({ button_continue_label: e.target.value || null })
            }
            placeholder={t("play.continue", "Continuer")}
            maxLength={40}
            className="h-9 rounded-lg text-[13px]"
            data-testid={`${p}-continue-label-input`}
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
            {t("game_editor.ready_label", "Texte du bouton « Prêt à jouer »")}
          </div>
          <Input
            value={game.button_ready_label || ""}
            onChange={(e) =>
              patch({ button_ready_label: e.target.value || null })
            }
            placeholder={t("play.ready", "Prêt à jouer ?")}
            maxLength={40}
            className="h-9 rounded-lg text-[13px]"
            data-testid={`${p}-ready-label-input`}
          />
        </div>
      </div>

      {/* Overlay intensity */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {t("game_editor.overlay_label", "Assombrissement du fond")}
          </div>
          <span
            className="text-xs font-semibold tabular-nums"
            data-testid={`${p}-overlay-value`}
          >
            {overlay}%
          </span>
        </div>
        <Slider
          value={[overlay]}
          min={0}
          max={100}
          step={5}
          onValueChange={([v]) => patch({ bg_overlay_opacity: v })}
          data-testid={`${p}-overlay-slider`}
        />
        <div className="mt-1 flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">
            {t(
              "game_editor.overlay_hint",
              "Voile sombre sur l'image de fond pour la lisibilité. 55 % par défaut."
            )}
          </p>
          {game.bg_overlay_opacity != null && (
            <button
              type="button"
              onClick={() => patch({ bg_overlay_opacity: null })}
              className="text-[11px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-4 shrink-0 ml-3"
              data-testid={`${p}-overlay-reset`}
            >
              {t("game_editor.color_reset", "Réinitialiser par défaut")}
            </button>
          )}
        </div>
      </div>

      {/* Forced theme */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
          {t("game_editor.theme_label", "Mode d'affichage")}
        </div>
        <Select
          value={game.force_theme || "auto"}
          onValueChange={(v) =>
            patch({ force_theme: v === "auto" ? null : v })
          }
        >
          <SelectTrigger
            className="h-9 rounded-lg text-[13px] w-[230px]"
            data-testid={`${p}-theme-select`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">
              {t("game_editor.theme_auto", "Auto (choix du joueur)")}
            </SelectItem>
            <SelectItem value="light">
              {t("game_editor.theme_light", "Clair forcé")}
            </SelectItem>
            <SelectItem value="dark">
              {t("game_editor.theme_dark", "Sombre forcé")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Confetti colors */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
          {t("game_editor.confetti_label", "Couleurs des confettis de victoire")}
        </div>
        {!game.confetti_colors?.length ? (
          <button
            type="button"
            onClick={() => patch({ confetti_colors: [...DEFAULT_CONFETTI] })}
            className="h-9 px-3 rounded-lg border border-border text-xs font-medium hover:border-foreground/30"
            data-testid={`${p}-confetti-enable`}
          >
            {t("game_editor.confetti_enable", "Personnaliser les confettis")}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {game.confetti_colors.map((c, i) => (
              <input
                key={i}
                type="color"
                value={c}
                onChange={(e) => {
                  const next = [...game.confetti_colors];
                  next[i] = e.target.value;
                  patch({ confetti_colors: next });
                }}
                className="h-9 w-9 rounded-md border border-border bg-transparent cursor-pointer"
                data-testid={`${p}-confetti-color-${i}`}
              />
            ))}
            <button
              type="button"
              onClick={() => patch({ confetti_colors: null })}
              className="h-9 w-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-destructive"
              aria-label={t("game_editor.color_reset", "Réinitialiser par défaut")}
              data-testid={`${p}-confetti-reset`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Wheel hub logo (spin wheel only) */}
      {game.type === "spin_wheel" && (
        <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
          <div>
            <div className="text-xs font-medium">
              {t(
                "game_editor.hub_logo_label",
                "Logo de l'enseigne au centre de la roue"
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {t(
                "game_editor.hub_logo_hint",
                "Remplace le texte SPIN par le logo de l'enseigne."
              )}
            </p>
          </div>
          <Switch
            checked={!!game.wheel_hub_logo}
            onCheckedChange={(v) => patch({ wheel_hub_logo: !!v })}
            data-testid={`${p}-hub-logo-toggle`}
          />
        </div>
      )}

      {/* Game sounds */}
      <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
        <div>
          <div className="text-xs font-medium">
            {t("game_editor.sounds_label", "Sons du jeu")}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t(
              "game_editor.sounds_hint",
              "Tic-tac de la roue + jingle de victoire. Le joueur peut couper le son."
            )}
          </p>
        </div>
        <Switch
          checked={!!game.game_sounds}
          onCheckedChange={(v) => patch({ game_sounds: !!v })}
          data-testid={`${p}-sounds-toggle`}
        />
      </div>

      {/* Player form fields */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
          {t("game_editor.form_section", "Formulaire joueur")}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              {t("game_editor.phone_field", "Champ téléphone")}
            </div>
            <Select
              value={game.collect_phone || "required"}
              onValueChange={(v) =>
                patch({ collect_phone: v === "required" ? null : v })
              }
            >
              <SelectTrigger
                className="h-9 rounded-lg text-[13px] w-[180px]"
                data-testid={`${p}-phone-select`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="required">
                  {t("game_editor.phone_required", "Obligatoire")}
                </SelectItem>
                <SelectItem value="optional">
                  {t("game_editor.phone_optional", "Facultatif")}
                </SelectItem>
                <SelectItem value="hidden">
                  {t("game_editor.phone_hidden", "Masqué")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Switch
              checked={!!game.collect_birthdate}
              onCheckedChange={(v) => patch({ collect_birthdate: !!v })}
              data-testid={`${p}-birthdate-toggle`}
            />
            <span className="text-xs font-medium">
              {t("game_editor.birthdate_label", "Demander la date de naissance")}
            </span>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              {t("game_editor.children_field", "Champ nombre d'enfants")}
            </div>
            <Select
              value={game.collect_children || "hidden"}
              onValueChange={(v) =>
                patch({ collect_children: v === "hidden" ? null : v })
              }
            >
              <SelectTrigger
                className="h-9 rounded-lg text-[13px] w-[180px]"
                data-testid={`${p}-children-select`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hidden">
                  {t("game_editor.phone_hidden", "Masqué")}
                </SelectItem>
                <SelectItem value="optional">
                  {t("game_editor.phone_optional", "Facultatif")}
                </SelectItem>
                <SelectItem value="required">
                  {t("game_editor.phone_required", "Obligatoire")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1">
              {t("game_editor.city_field", "Champ ville")}
            </div>
            <Select
              value={game.collect_city || "hidden"}
              onValueChange={(v) =>
                patch({ collect_city: v === "hidden" ? null : v })
              }
            >
              <SelectTrigger
                className="h-9 rounded-lg text-[13px] w-[180px]"
                data-testid={`${p}-city-select`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hidden">
                  {t("game_editor.phone_hidden", "Masqué")}
                </SelectItem>
                <SelectItem value="optional">
                  {t("game_editor.phone_optional", "Facultatif")}
                </SelectItem>
                <SelectItem value="required">
                  {t("game_editor.phone_required", "Obligatoire")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Win image / GIF + Social share (OG) image */}
      <div className="grid grid-cols-2 gap-4 pt-1">
        <div>
          <BackgroundUploader
            variant="win"
            allowGif
            label={t("game_editor.win_image_label", "Image / GIF de victoire")}
            value={game.win_image}
            onChange={(v) => patch({ win_image: v })}
            testIdPrefix={`${p}-win-image`}
          />
        </div>
        <div>
          <BackgroundUploader
            variant="og"
            label={t("game_editor.og_image_label", "Image de partage (réseaux)")}
            value={game.og_image}
            onChange={(v) => patch({ og_image: v })}
            testIdPrefix={`${p}-og-image`}
          />
        </div>
      </div>
    </div>
  );
}
